import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "../../assets/styles/colors.css";

const inputClass =
  "w-full px-3 py-2 bg-white dark:bg-black border border-[var(--color-border)] rounded-lg text-slate-900 dark:text-[var(--color-text-white)] text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-[var(--color-primary)] transition-colors";
const inputErrorClass =
  "w-full px-3 py-2 bg-white dark:bg-black border border-red-500 rounded-lg text-slate-900 dark:text-[var(--color-text-white)] text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none transition-colors";

// Mirror of backend MAX_METADATA_KEY_LENGTH and _VALID_KEY_PATTERN.
const MAX_KEY_LENGTH = 64;
const VALID_KEY_PATTERN = /^[\p{L}\p{N}_][\p{L}\p{N}_\-. ]*$/u;

// Lightweight mirror of backend _HARD_BLOCK_PATTERNS from sanitization.py.
// Not exhaustive — server-side validator is the authoritative gate. This
// only catches the obvious paste-from-attacker case and surfaces it inline
// so the user doesn't have to submit and get bounced.
const CLIENT_INJECTION_PATTERNS = [
  /(ignore|disregard|forget|override|bypass)\s+(the\s+)?(previous|above|all|your|prior|earlier|system|safety)\s+(instructions?|rules?|prompts?|guidelines?|constraints?|directives?)/i,
  /(ignore|disregard|forget)\s+(all\s+)?(previous\s+)?instructions/i,
  /(you\s+are\s+now|act\s+as|pretend\s+(you\s+are|to\s+be)|new\s+role\s*:|system\s*:|assistant\s*:)/i,
  /(<\/?system>|<\|im_(end|start)\|>|<\|endoftext\|>|\[\/?INST\]|\[\/?SYS\])/i,
  /(cat|print|echo|dump|leak|reveal)\s+.{0,30}(\.env|secret|credential|password|token|api.?key)/i,
  /(os\.environ|process\.env|subprocess|__import__|__builtins__)/i,
];

function validateKey(key) {
  if (!key || !key.trim()) return null; // Empty — don't error, row will be dropped on save.
  if (key.length > MAX_KEY_LENGTH) {
    return `Key too long (max ${MAX_KEY_LENGTH} chars)`;
  }
  if (!VALID_KEY_PATTERN.test(key)) {
    return "Only letters, digits, underscore, dash, dot, space allowed";
  }
  for (const pattern of CLIENT_INJECTION_PATTERNS) {
    if (pattern.test(key)) {
      return "Key contains disallowed content";
    }
  }
  return null;
}

function validateValue(value) {
  if (!value) return null;
  for (const pattern of CLIENT_INJECTION_PATTERNS) {
    if (pattern.test(value)) {
      return "Value contains disallowed content";
    }
  }
  return null;
}

function PropertiesModal({
  show,
  title,
  subtitle,
  data,
  excludeKeys = [],
  preserveKeys = {},
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    if (show) {
      if (data) {
        const pairs = Object.entries(data)
          .filter(
            ([key, value]) =>
              !excludeKeys.includes(key) && value !== "" && value !== undefined,
          )
          .map(([key, value]) => ({ key, value: String(value) }));
        setProperties(pairs);
      } else {
        setProperties([]);
      }
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddProperty = () =>
    setProperties([...properties, { key: "", value: "" }]);
  const handleRemoveProperty = (index) =>
    setProperties(properties.filter((_, i) => i !== index));
  const handlePropertyChange = (index, field, value) => {
    const updated = [...properties];
    updated[index][field] = value;
    setProperties(updated);
  };

  const rowErrors = properties.map(({ key, value }) => ({
    key: validateKey(key),
    value: validateValue(value),
  }));
  const hasErrors = rowErrors.some((r) => r.key || r.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasErrors) return;
    const newData = { ...preserveKeys };
    properties.forEach(({ key, value }) => {
      if (key.trim()) newData[key.trim()] = value;
    });
    onSave(newData);
    onClose();
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md mx-4 rounded-xl border border-[var(--color-border)] bg-white dark:bg-black shadow-[0_0_20px_var(--color-shadow-primary)]">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--color-text-white)]">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-1 text-sm text-slate-600 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 dark:text-gray-400 hover:text-slate-900 dark:hover:text-[var(--color-text-white)] hover:bg-[var(--color-primary)]/10 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-4 max-h-[60vh] overflow-y-auto"
        >
          {properties.length === 0 ? (
            <p className="text-center text-slate-500 dark:text-gray-400 text-sm py-4">
              {t("No properties. Click Add to create one.")}
            </p>
          ) : (
            <div className="space-y-3">
              {properties.map((prop, index) => {
                const err = rowErrors[index];
                return (
                <div key={index} className="flex flex-col gap-1">
                  <div className="flex gap-2 items-start">
                    <input
                      type="text"
                      value={prop.key}
                      maxLength={MAX_KEY_LENGTH}
                      onChange={(e) =>
                        handlePropertyChange(index, "key", e.target.value)
                      }
                      className={`${err.key ? inputErrorClass : inputClass} flex-1`}
                      placeholder="Key"
                    />
                    <input
                      type="text"
                      value={prop.value}
                      onChange={(e) =>
                        handlePropertyChange(index, "value", e.target.value)
                      }
                      className={`${err.value ? inputErrorClass : inputClass} flex-1`}
                      placeholder="Value"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveProperty(index)}
                      className="p-2 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                  {(err.key || err.value) && (
                    <p className="text-xs text-red-500 pl-1">
                      {err.key || err.value}
                    </p>
                  )}
                </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={handleAddProperty}
            className="w-full py-2.5 border border-dashed border-[var(--color-primary)]/40 text-[var(--color-primary)] rounded-lg text-sm hover:bg-[var(--color-primary)]/10 hover:border-[var(--color-primary)]/60 transition-colors flex items-center justify-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t("Add Property")}
          </button>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-[var(--color-border)] text-slate-600 dark:text-gray-400 bg-white dark:bg-[var(--color-bg-card)] rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)] transition-colors"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={hasErrors}
              className="flex-1 px-4 py-2.5 bg-[var(--color-primary)] border border-[var(--color-primary)] text-white dark:text-slate-900 rounded-lg text-sm font-semibold hover:shadow-[0_0_10px_var(--color-shadow-primary-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
            >
              {t("Save")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

PropertiesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  data: PropTypes.object,
  excludeKeys: PropTypes.arrayOf(PropTypes.string),
  preserveKeys: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default PropertiesModal;
